<template>
  <control-wrapper
    v-bind="controlWrapper"
    :styles="styles"
    :isFocused="isFocused"
    :appliedOptions="appliedOptions"
  >
    <v-hover v-slot="{ hover }">
      <v-autocomplete
        v-disabled-icon-focus
        :id="control.id + '-input'"
        :class="styles.control.input"
        :disabled="!control.enabled"
        :autofocus="appliedOptions.focus"
        :placeholder="appliedOptions.placeholder"
        :label="computedLabel"
        :hint="control.description"
        :persistent-hint="persistentHint()"
        :required="control.required"
        :error-messages="control.errors"
        :maxlength="
          appliedOptions.restrict ? control.schema.maxLength : undefined
        "
        :counter="
          control.schema.maxLength !== undefined
            ? control.schema.maxLength
            : undefined
        "
        :clearable="hover"
        :items="virtualItems"
        :search-input.sync="search"
        item-text="address"
        return-object
        :loading="isLoading"
        :hide-no-data="isLoading || !search"
        v-bind="vuetifyProps('v-autocomplete')"
        attach
        v-model="selected"
        @focus="isFocused = true"
        @blur="isFocused = false"
      >
        <template v-slot:no-data>
          <v-list-item-content>
            <v-list-item-title v-text="noResultsFoundMessage" />
          </v-list-item-content>
        </template>

        <template v-slot:item="{ item }">
          <v-list-item-avatar>
            <v-icon>mdi-map-marker-outline</v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title v-text="item.address" />
          </v-list-item-content>
        </template>
      </v-autocomplete>
    </v-hover>
  </control-wrapper>
</template>

<script lang="ts">
import { Loader } from '@googlemaps/js-api-loader';
import {
  and,
  ControlElement,
  hasType,
  isObjectControl,
  isStringControl,
  JsonFormsRendererRegistryEntry,
  JsonFormsSubStates,
  optionIs,
  or,
  rankWith,
} from '@jsonforms/core';
import {
  rendererProps,
  RendererProps,
  useJsonFormsControl,
} from '@jsonforms/vue2';
import {
  ControlWrapper,
  useTranslator,
  useVuetifyControl,
} from '@jsonforms/vue2-vuetify';
import debouncePromise from 'debounce-promise';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import { defineComponent, ref, inject } from 'vue';
import {
  VAutocomplete,
  VHover,
  VIcon,
  VListItemAvatar,
  VListItemContent,
  VListItemTitle,
} from 'vuetify/lib';
import { DisabledIconFocus } from './directives';

const controlRenderer = defineComponent({
  name: 'address-control-renderer',
  components: {
    ControlWrapper,
    VHover,
    VAutocomplete,
    VIcon,
    VListItemAvatar,
    VListItemContent,
    VListItemTitle,
  },
  directives: {
    DisabledIconFocus,
  },
  props: {
    ...rendererProps<ControlElement>(),
  },
  setup(props: RendererProps<ControlElement>) {
    const t = useTranslator();

    const control = useVuetifyControl(
      useJsonFormsControl(props),
      (value) => value || undefined
    );

    const isLoading = ref(false);
    const items = ref<
      Array<{ name?: string; address: string; placeId?: string }>
    >([]);
    let data = control.control.value.data;
    if (isObject(data)) {
      const key = control.control.value.uischema?.options?.['key'] || 'formatted_address';
      data = get(data, key);
    }
    const search = ref<string | null>(data);
    const autocomplete = ref<google.maps.places.AutocompleteService | null>(
      null
    );
    const places = ref<google.maps.places.PlacesService | null>(null);
    const initialized = ref(false);

    const jsonforms = inject<JsonFormsSubStates>('jsonforms');
    if (!jsonforms) {
      throw new Error(
        "'jsonforms' couldn't be injected. Are you within JsonForms?"
      );
    }

    return {
      ...control,
      t,
      isLoading,
      items,
      search,
      autocomplete,
      places,
      initialized,
      jsonforms,
    };
  },
  computed: {
    selected: {
      get(): any {
        let value = this.control.data;
        if (isObject(value)) {
          value = get(value, this.key);
        }
        return value;
      },
      async set(item: {
        name?: string;
        address: string;
        placeId?: string;
        data?: any;
      }) {
        let result: any = item;
        if (item?.placeId) {
          result = await this.getDetails(item);

          if (this.isStringControl) {
            result = get(result, this.key);
          }
        }

        this.onChange(result);
      },
    },
    key(): string {
      return this.uischema?.options?.['key'] || 'formatted_address';
    },
    isStringControl(): boolean {
      return (
        !isEmpty(this.control.schema) && hasType(this.control.schema, 'string')
      );
    },
    // If we saved the value but not the items, ensure that we spoof an items array
    // with at least the value so that the component still works
    virtualItems(): Array<{
      name?: string;
      address: string;
      placeId?: string;
    }> {
      let selected:
        | {
            name?: string;
            address: string;
            placeId?: string;
          }
        | undefined = undefined;
      if (this.control.data) {
        let address = this.control.data;
        if (isObject(address)) {
          address = get(address, this.key);
        }
        selected = { address: address };
      }

      return selected ? [...this.items, selected] : this.items;
    },
    noResultsFoundMessage(): string {
      return this.t('No results found', 'No results found');
    },
    restrictToCountries(): Array<string> {
      let contries =
        this.jsonforms.config?.RUNBUGGY_FORMS_RENDERER_CONFIG?.googleMaps
          ?.countries;
      if (Array.isArray(contries)) {
        // prefer config
        return contries;
      }

      contries = (window as any)?.CONFIG?.google?.maps?.countries;

      if (Array.isArray(contries)) {
        // prefer spa-v2 countries
        return contries;
      }

      contries = (window as any)?.RUNBUGGY_FORMS_RENDERER_CONFIG?.googleMaps
        ?.countries;
      if (Array.isArray(contries)) {
        return contries;
      }

      // default
      return ['us'];
    },
    apiKey(): string {
      let key =
        this.jsonforms.config?.RUNBUGGY_FORMS_RENDERER_CONFIG?.googleMaps
          ?.apiKey;
      if (isString(key)) {
        // prefer config
        return key;
      }

      key = (window as any)?.CONFIG?.google?.maps?.key;
      if (isString(key)) {
        // prefer spa-v2 key
        return key;
      }

      return (window as any)?.RUNBUGGY_FORMS_RENDERER_CONFIG?.googleMaps
        ?.apiKey;
    },
  },
  watch: {
    async search(text) {
      if (text) {
        this.isLoading = true;

        this.searchPlaces(text)
          .then(({ predictions, status }) => {
            const autocompleteError =
              status != google.maps.places.PlacesServiceStatus.OK;
            if (!autocompleteError) {
              this.items = predictions.map((p) => ({
                name: p.structured_formatting.main_text,
                address: p.description,
                placeId: p.place_id,
              }));
            } else {
              this.items = [];
            }
            this.isLoading = false;
          })
          .catch((_) => {
            this.items = [];
            this.isLoading = false;
          });
      }
    },
  },
  async created() {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      const loader = new Loader({
        apiKey: this.apiKey,
        libraries: ['places', 'drawing'],
      });

      await loader.load();
    }

    this.places = new google.maps.places.PlacesService(
      document.createElement('div')
    );
    this.autocomplete = new google.maps.places.AutocompleteService();
    this.initialized = true;
    this.$emit('initialized');
  },
  methods: {
    // copy the object conversion from legacy
    toObjectValue(place: google.maps.places.PlaceResult) {
      let fields = [
        'street_address',
        'route',
        'intersection',
        'political',
        'country',
        'administrative_area_level_1',
        'administrative_area_level_2',
        'administrative_area_level_3',
        'administrative_area_level_4',
        'administrative_area_level_5',
        'colloquial_area',
        'locality',
        'sublocality',
        'neighborhood',
        'premise',
        'subpremise',
        'postal_code',
        'natural_feature',
        'airport',
        'park',
        'point_of_interest',
      ];

      let value = {} as any;

      let components = get(place, 'address_components', []);
      components.forEach((component) => {
        fields.forEach((field) => {
          let componentIndex = component.types.indexOf(field);

          if (componentIndex > -1) {
            value[`${field}_short_name`] = component['short_name'];
            value[`${field}_long_name`] = component['long_name'];
          }
        });
      });
      value.formatted_address = place.formatted_address;
      const location = place.geometry?.location;
      if (location) {
        value.latitude = location.lat();
        value.longitude = location.lng();
      }

      return value;
    },
    getDetails(item: {
      name?: string;
      address: string;
      placeId?: string;
      data?: any;
    }) {
      return new Promise(
        (resolve: (value: Record<string, any>) => void, reject) => {
          if (item.placeId) {
            if (!this.places) {
              reject('Places not initialized');
              return;
            }
            this.places.getDetails(
              { placeId: item.placeId },
              (place, status) => {
                const error =
                  status != google.maps.places.PlacesServiceStatus.OK;
                if (error) {
                  reject(status);
                  return;
                }
                if (place) {
                  resolve(this.toObjectValue(place));
                } else {
                  reject('Null place');
                }
              }
            );
          } else {
            resolve(item.data);
          }
        }
      );
    },
    searchPlaces(text: string) {
      return debouncePromise(() => {
        return new Promise(
          (
            resolve: (value: {
              predictions: google.maps.places.AutocompletePrediction[];
              status: google.maps.places.PlacesServiceStatus;
            }) => void,
            reject
          ) => {
            if (!this.autocomplete) {
              reject('Autocomplete not initialized');
              return;
            }
            this.autocomplete.getPlacePredictions(
              {
                input: text,
                componentRestrictions: { country: this.restrictToCountries },
              },
              (predictions, status) => {
                if (predictions) {
                  resolve({ predictions, status });
                } else {
                  reject('Null predictions');
                }
              }
            );
          }
        );
      }, 500)();
    },
  },
});

export default controlRenderer;

export const entry: JsonFormsRendererRegistryEntry = {
  renderer: controlRenderer,
  tester: rankWith(
    2,
    or(
      and(isStringControl, optionIs('format', 'address')),
      and(isObjectControl, optionIs('format', 'address'))
    )
  ),
};
</script>
